import React, { useState, useEffect, useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { fetchPoolHistory } from '../api';
import { useTheme } from '../theme';
import { useIsMobile } from '../useMediaQuery';
import ProGate from './ProGate';

function computeSMA(data, window) {
  return data.map((d, i) => {
    if (i < window - 1) return { ...d, sma: null };
    let sum = 0;
    for (let j = i - window + 1; j <= i; j++) sum += data[j].apy;
    return { ...d, sma: sum / window };
  });
}

function computeVolatility(data, window) {
  return data.map((d, i) => {
    if (i < window - 1) return { ...d, upper: null, lower: null };
    const slice = data.slice(i - window + 1, i + 1).map(x => x.apy);
    const mean = slice.reduce((s, v) => s + v, 0) / window;
    const variance = slice.reduce((s, v) => s + Math.pow(v - mean, 2), 0) / window;
    const std = Math.sqrt(variance);
    return { ...d, upper: mean + 2 * std, lower: Math.max(0, mean - 2 * std) };
  });
}

function linearRegression(data) {
  const n = data.length;
  if (n < 2) return { slope: 0, intercept: 0, predict: () => 0 };
  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
  data.forEach((d, i) => {
    sumX += i;
    sumY += d.apy;
    sumXY += i * d.apy;
    sumX2 += i * i;
  });
  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;
  return {
    slope,
    intercept,
    predict: (x) => Math.max(0, slope * x + intercept),
  };
}

function stabilityScore(data) {
  if (data.length < 7) return null;
  const apys = data.slice(-30).map(d => d.apy);
  const mean = apys.reduce((s, v) => s + v, 0) / apys.length;
  if (mean === 0) return 0;
  const cv = Math.sqrt(apys.reduce((s, v) => s + Math.pow(v - mean, 2), 0) / apys.length) / mean;
  return Math.max(0, Math.round((1 - Math.min(cv, 1)) * 100));
}

export default function AprTrendPanel({ pool }) {
  const { colors, dark } = useTheme();
  const isMobile = useIsMobile();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [range, setRange] = useState(30);

  useEffect(() => {
    if (!pool?.poolId) return;
    setLoading(true);
    setError(null);
    fetchPoolHistory(pool.poolId)
      .then(data => {
        setHistory(data || []);
        setLoading(false);
      })
      .catch((err) => {
        setError('Failed to load trend data. Please try again later.');
        setLoading(false);
      });
  }, [pool?.poolId]);

  const chartData = useMemo(() => {
    if (!history.length) return [];

    const sliced = history.slice(-range);
    const withSma7 = computeSMA(sliced, 7);
    const withSma30 = range >= 30 ? computeSMA(sliced, Math.min(30, sliced.length)) : withSma7;
    const withBands = computeVolatility(sliced, Math.min(14, sliced.length));
    const reg = linearRegression(sliced);

    return sliced.map((d, i) => ({
      date: new Date(d.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      apy: d.apy,
      sma7: withSma7[i]?.sma,
      sma30: range >= 30 ? withSma30[i]?.sma : null,
      upper: withBands[i]?.upper,
      lower: withBands[i]?.lower,
      trend: reg.predict(i),
    }));
  }, [history, range]);

  const score = useMemo(() => stabilityScore(history), [history]);

  const prediction = useMemo(() => {
    if (history.length < 7) return null;
    const sliced = history.slice(-30);
    const reg = linearRegression(sliced);
    const current = history[history.length - 1]?.apy || 0;
    const predicted7d = reg.predict(sliced.length + 7 - 1);
    return {
      current,
      predicted7d,
      direction: predicted7d > current ? 'up' : predicted7d < current ? 'down' : 'stable',
    };
  }, [history]);

  const content = (
    <div style={{
      background: colors.surface,
      borderRadius: 12,
      border: `1px solid ${colors.border}`,
      padding: isMobile ? 16 : 24,
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 16,
        flexWrap: 'wrap',
        gap: 8,
      }}>
        <div>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 10,
            fontWeight: 600,
            color: colors.labelLight,
            letterSpacing: 1.2,
          }}>
            APR TREND & PREDICTION
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 9,
              fontWeight: 600,
              color: '#8A6A00',
              background: dark ? 'rgba(138,106,0,0.15)' : '#FFF8E0',
              border: `1px solid ${dark ? 'rgba(138,106,0,0.3)' : '#E0CC70'}`,
              padding: '1px 6px',
              borderRadius: 3,
            }}>
              PRO
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 4 }}>
          {[7, 30, 90].map(d => (
            <button
              key={d}
              onClick={() => setRange(d)}
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 10,
                fontWeight: range === d ? 600 : 400,
                color: range === d ? colors.green : colors.textSecondary,
                background: range === d ? colors.greenBg : 'transparent',
                border: `1px solid ${range === d ? colors.greenBorder : 'transparent'}`,
                borderRadius: 6,
                padding: '4px 10px',
                cursor: 'pointer',
              }}
            >
              {d}D
            </button>
          ))}
        </div>
      </div>

      {/* Stats row */}
      <div style={{
        display: 'flex',
        gap: 12,
        marginBottom: 16,
        flexWrap: 'wrap',
      }}>
        {score !== null && (
          <div style={{
            background: dark ? 'rgba(255,255,255,0.04)' : '#FAFAFA',
            borderRadius: 8,
            padding: '10px 14px',
            flex: '1 1 100px',
          }}>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 9,
              color: colors.labelLight,
              letterSpacing: 1,
              marginBottom: 4,
            }}>
              STABILITY
            </div>
            <div style={{
              fontFamily: "'DM Serif Display', serif",
              fontSize: 22,
              color: score >= 70 ? colors.green : score >= 40 ? '#8A6A00' : '#B8001A',
            }}>
              {score}
              <span style={{ fontSize: 12, fontFamily: "'IBM Plex Mono', monospace" }}>/100</span>
            </div>
          </div>
        )}

        {prediction && (
          <div style={{
            background: dark ? 'rgba(255,255,255,0.04)' : '#FAFAFA',
            borderRadius: 8,
            padding: '10px 14px',
            flex: '1 1 120px',
          }}>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 9,
              color: colors.labelLight,
              letterSpacing: 1,
              marginBottom: 4,
            }}>
              7D PREDICTION
            </div>
            <div style={{
              fontFamily: "'DM Serif Display', serif",
              fontSize: 22,
              color: prediction.direction === 'up' ? colors.green : prediction.direction === 'down' ? '#B8001A' : colors.textPrimary,
            }}>
              {prediction.direction === 'up' ? '\u2191' : prediction.direction === 'down' ? '\u2193' : '\u2192'}
              {prediction.predicted7d.toFixed(2)}%
            </div>
          </div>
        )}

        {chartData.length > 0 && (
          <div style={{
            background: dark ? 'rgba(255,255,255,0.04)' : '#FAFAFA',
            borderRadius: 8,
            padding: '10px 14px',
            flex: '1 1 100px',
          }}>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 9,
              color: colors.labelLight,
              letterSpacing: 1,
              marginBottom: 4,
            }}>
              {range}D AVG
            </div>
            <div style={{
              fontFamily: "'DM Serif Display', serif",
              fontSize: 22,
              color: colors.textPrimary,
            }}>
              {(chartData.reduce((s, d) => s + d.apy, 0) / chartData.length).toFixed(2)}%
            </div>
          </div>
        )}
      </div>

      {/* Chart */}
      {error ? (
        <div style={{
          height: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 12,
          color: '#B8001A',
        }}>
          {error}
        </div>
      ) : loading ? (
        <div style={{
          height: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 12,
          color: colors.labelMuted,
        }}>
          Loading trend data...
        </div>
      ) : chartData.length === 0 ? (
        <div style={{
          height: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 12,
          color: colors.labelMuted,
        }}>
          No historical data available
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="aprGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={colors.green} stopOpacity={0.15} />
                <stop offset="95%" stopColor={colors.green} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="bandGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8A6A00" stopOpacity={0.1} />
                <stop offset="95%" stopColor="#8A6A00" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={dark ? 'rgba(255,255,255,0.06)' : '#F0F0F0'} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 9, fontFamily: "'IBM Plex Mono', monospace", fill: colors.labelMuted }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 9, fontFamily: "'IBM Plex Mono', monospace", fill: colors.labelMuted }}
              axisLine={false}
              tickLine={false}
              tickFormatter={v => `${v.toFixed(1)}%`}
            />
            <Tooltip
              contentStyle={{
                background: dark ? colors.surface : '#FFFFFF',
                border: `1px solid ${colors.border}`,
                borderRadius: 8,
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 11,
              }}
              formatter={(val, name) => {
                if (val == null) return ['-', name];
                const labels = { apy: 'APR', sma7: 'SMA 7', sma30: 'SMA 30', trend: 'Trend', upper: 'Upper Band', lower: 'Lower Band' };
                return [`${val.toFixed(2)}%`, labels[name] || name];
              }}
            />
            {/* Volatility bands */}
            <Area type="monotone" dataKey="upper" stroke="none" fill="url(#bandGrad)" />
            <Area type="monotone" dataKey="lower" stroke="none" fill="transparent" />
            {/* Main APR */}
            <Area type="monotone" dataKey="apy" stroke={colors.green} fill="url(#aprGrad)" strokeWidth={2} dot={false} />
            {/* SMA lines */}
            <Area type="monotone" dataKey="sma7" stroke="#E8471A" fill="none" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
            {range >= 30 && (
              <Area type="monotone" dataKey="sma30" stroke="#5470D4" fill="none" strokeWidth={1.5} strokeDasharray="6 3" dot={false} />
            )}
            {/* Trend line */}
            <Area type="monotone" dataKey="trend" stroke="#8A6A00" fill="none" strokeWidth={1} strokeDasharray="2 2" dot={false} opacity={0.6} />
          </AreaChart>
        </ResponsiveContainer>
      )}

      {/* Legend */}
      <div style={{
        display: 'flex',
        gap: 16,
        marginTop: 12,
        flexWrap: 'wrap',
        justifyContent: 'center',
      }}>
        {[
          { color: colors.green, label: 'APR', dash: false },
          { color: '#E8471A', label: 'SMA 7', dash: true },
          ...(range >= 30 ? [{ color: '#5470D4', label: 'SMA 30', dash: true }] : []),
          { color: '#8A6A00', label: 'Trend', dash: true },
        ].map(l => (
          <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{
              width: 16,
              height: 2,
              background: l.color,
              borderRadius: 1,
              ...(l.dash ? { backgroundImage: `repeating-linear-gradient(90deg, ${l.color} 0 4px, transparent 4px 8px)`, background: 'transparent' } : {}),
            }} />
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 9,
              color: colors.labelMuted,
            }}>
              {l.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <ProGate feature="APR trend analysis & prediction">
      {content}
    </ProGate>
  );
}
